/**
 * ENG-5116 — track consecutive ticks where an agent's persistent session
 * is unhealthy AND we didn't attempt a respawn this tick.
 *
 * Vigil's incident: day-rollover stopped the tmux session at 00:02, then
 * for >6 hours the manager polled every ~30s but the spawn branch never
 * fired and never logged why. The bulk-heartbeat update at the API kept
 * `last_heartbeat_at` fresh, so neither the customer nor the cloud
 * heartbeat alarm caught it. This tracker is the local-manager half of
 * the answer: once a session has been unhealthy without a respawn
 * attempt for `threshold` consecutive ticks, log a loud WARN so the
 * operator (or future Ops agent / synthetic prober) can correlate.
 *
 * Pure state held in a Map — no I/O, no timers — so it's safe to unit
 * test and the manager's per-tick code drives it.
 */

export interface StuckTickInput {
  codeName: string;
  sessionHealthy: boolean;
  spawnAttempted: boolean;
}

export interface StuckTickResult {
  /** Number of consecutive ticks the agent has been unhealthy with no spawn. */
  consecutiveTicks: number;
  /** True on the tick we cross the warning threshold (caller logs). */
  shouldWarn: boolean;
  /** True when the counter just reset (caller can log recovery if useful). */
  recovered: boolean;
}

/**
 * Default WARN threshold: 3 consecutive ticks. At a ~30s poll interval
 * that's ~90s of an agent being silently down, which beats the 6h Vigil
 * outage by several orders of magnitude without being so noisy that
 * single transient blips trigger pages.
 */
export const DEFAULT_STUCK_WARN_THRESHOLD = 3;

export class PersistentSessionStuckTracker {
  private readonly counts = new Map<string, number>();
  private readonly warned = new Set<string>();
  private readonly threshold: number;

  constructor(threshold: number = DEFAULT_STUCK_WARN_THRESHOLD) {
    if (threshold < 1) throw new Error('stuck-tracker threshold must be >= 1');
    this.threshold = threshold;
  }

  /**
   * Record one tick for one agent. Returns whether the caller should log
   * a stuck-warning this tick. Re-warning only fires when the counter
   * climbs above the threshold *again* after a recovery, so a stuck
   * agent doesn't spam the log every 30s — operators get one WARN per
   * stuck streak.
   */
  record(input: StuckTickInput): StuckTickResult {
    const { codeName, sessionHealthy, spawnAttempted } = input;
    const wasStuck = (this.counts.get(codeName) ?? 0) >= this.threshold;

    // Recovery condition: either the session is healthy, OR we made a
    // genuine respawn attempt this tick. Both reset the streak; the
    // attempt path matters because the next tick will observe whether
    // it actually came up.
    if (sessionHealthy || spawnAttempted) {
      this.counts.delete(codeName);
      const wasWarned = this.warned.delete(codeName);
      return {
        consecutiveTicks: 0,
        shouldWarn: false,
        recovered: wasStuck && wasWarned,
      };
    }

    const next = (this.counts.get(codeName) ?? 0) + 1;
    this.counts.set(codeName, next);

    // Only warn on the first tick that crosses the threshold — set
    // membership in `warned` debounces re-fires until a recovery.
    const crossed = next >= this.threshold && !this.warned.has(codeName);
    if (crossed) this.warned.add(codeName);

    return {
      consecutiveTicks: next,
      shouldWarn: crossed,
      recovered: false,
    };
  }

  /** Test/debug helper: peek at an agent's current streak. */
  peek(codeName: string): number {
    return this.counts.get(codeName) ?? 0;
  }

  /** Test helper: clear all state. */
  reset(): void {
    this.counts.clear();
    this.warned.clear();
  }
}
